import { DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AppState,
  ChatDataActions,
  selectActiveChat,
  selectActiveChatFiles,
  selectActiveShortcut,
  selectDataOrigin,
  selectDeletingHistoryFile,
  selectTempFiles,
} from '@iese-chatbot/chat-data-access';
import { Chat, ChatFile, DataOrigin, FileMimetypePipe, FileSizePipe, Logger } from '@iese-chatbot/common-utils';
import { FileIconDirective } from '@iese-chatbot/ui-design-system';
import { Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ToastModule } from 'primeng/toast';
import { combineLatest } from 'rxjs';
import { UploadFilesButtonComponent } from '../upload-files-button/upload-files-button.component';

@Component({
  selector: 'icbc-upload-files-list',
  standalone: true,
  templateUrl: './upload-files-list.component.html',
  styles: [':host { width: 100%; svg {width: 24px; height: 24px;}}'],
  imports: [
    ChipModule,
    OverlayPanelModule,
    FileSizePipe,
    DatePipe,
    FileMimetypePipe,
    ScrollPanelModule,
    ToastModule,
    FileIconDirective,
    UploadFilesButtonComponent,
    ConfirmDialogModule,
  ],
})
export class UploadFilesListComponent {
  @Input() mode: 'full' | 'compact' = 'compact';
  public tempFiles: File[] = [];
  public chat: Chat | undefined;
  public chatFiles: ChatFile[] = [];
  public deleting = false;
  protected selectedFile: File | ChatFile | null = null;
  protected visible = false;

  constructor(private store: Store<AppState>, private readonly confirmationService: ConfirmationService) {
    this.store
      .select(selectDeletingHistoryFile)
      .pipe(takeUntilDestroyed())
      .subscribe((deleting) => {
        this.deleting = deleting;
      });
    this.store
      .select(selectActiveChat)
      .pipe(takeUntilDestroyed())
      .subscribe((chat) => {
        this.chat = chat;
        this.chatFiles = chat?.files ? chat.files.slice() : [];
      });
    this.store
      .select(selectActiveChatFiles)
      .pipe(takeUntilDestroyed())
      .subscribe((chatFiles) => {
        this.chatFiles = chatFiles ? chatFiles.slice() : [];
      });
    this.store
      .select(selectTempFiles)
      .pipe(takeUntilDestroyed())
      .subscribe((tempFiles) => {
        this.tempFiles = tempFiles ? tempFiles.slice() : [];
      });
    combineLatest([this.store.select(selectDataOrigin), this.store.select(selectActiveShortcut)])
      .pipe(takeUntilDestroyed())
      .subscribe(([dataOrigin, shortcut]) => {
        this.visible =
          (this.mode === 'full' && dataOrigin === DataOrigin.FILES && !shortcut) ||
          (this.mode === 'compact' && !!shortcut && shortcut.allowsFiles);
      });
  }

  protected showFileInfo(file: File | ChatFile, overlay: OverlayPanel, event?: Event) {
    if (event) {
      overlay.hide();
      this.selectedFile = file;
      overlay.show(event);
    }
  }

  protected hideFileInfo(overlay: OverlayPanel) {
    overlay.hide();
  }

  protected refreshOverlay(file: File | ChatFile, overlay: OverlayPanel, event?: Event) {
    if (!this.selectedFile || this.selectedFile !== file) {
      this.showFileInfo(file, overlay, event);
    }
  }

  protected removeTempFile(file: File, overlay: OverlayPanel) {
    this.hideFileInfo(overlay);
    this.confirmationService.confirm({
      header: 'Please, confirm',
      message: `Are you sure you want to remove the following file?<br/><strong>${file.name}</strong>`,
      icon: 'pi pi-trash',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.store.dispatch(ChatDataActions.removeFile({ file }));
      },
      reject: () => {
        this.store.dispatch({ type: 'NO_OP_TEMP_FILE_REMOVAL_REJECTED_BY_USER' });
      },
    });
  }

  protected deleteHistoryFile(file: ChatFile) {
    if (!this.chat) {
      Logger.error("Can't delete file, chat is undefined");
      return;
    }

    // TODO mover a effect igual que la confirmación de "chat delete"
    this.confirmationService
      .confirm({
        header: 'Please, confirm',
        message: `Are you sure you want to delete the following file?<br/><strong>${file.name}</strong>`,
        icon: 'pi pi-trash',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        accept: () => {
          if (this.chat) this.store.dispatch(ChatDataActions.deleteHistoryFile({ chat: this.chat, file }));
          else this.store.dispatch({ type: 'NO_OP_HIST_FILE_DELETION_NO_CHAT_ACTIVE' });
        },
        reject: () => {
          this.store.dispatch({ type: 'NO_OP_HIST_FILE_DELETION_REJECTED_BY_USER' });
        },
      })
      .requireConfirmation$.subscribe();
  }
}
